import { useState, useEffect } from "react";
import { toast } from "sonner";
import { g, p, eur, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Banknote, CreditCard, MoreHorizontal, Printer, UserPlus, Undo2 } from "lucide-react";
import { DividiConto } from "@/components/DividiConto";
import { PassaIncasso } from "@/components/PassaIncasso";

export default function Conto({ open, onClose, order, reload, onClosed }) {
  const { user, settings } = useApp();
  const { t } = useI18n();
  const [amount, setAmount] = useState("");
  const [disc, setDisc] = useState("");
  const [reason, setReason] = useState("");
  const [q, setQ] = useState("");
  const [found, setFound] = useState([]);

  useEffect(() => {
    if (open) setAmount(String(order.remaining));
  }, [open, order?.remaining]);

  if (!order) return null;

  const addDiscount = async (v) => {
    try {
      await p(`/orders/${order.id}/discount`, { amount: Number(v), reason, by: user.name });
      setDisc("");
      setReason("");
      toast.success(t("b.discountOk"));
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const pay = async (method, value, extra = {}) => {
    const amt = Number(value || amount);
    if (!amt || amt <= 0) return toast.error(t("b.badAmount"));
    try {
      await p(`/orders/${order.id}/payments`, { method, amount: amt, by: user.name, ...extra });
      toast.success(t("b.paidOk", { amount: eur(amt), method }));
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const search = async (v) => {
    setQ(v);
    setFound(v.length > 1 ? await g("/customers", { q: v }) : []);
  };

  const printBill = () => {
    const w = window.open("", "_blank", "width=420,height=640");
    if (!w) return toast.error(t("b.popups"));
    const rows = order.items
      .map((i) => `<tr><td>${i.qty}× ${i.name}${i.note ? ` <i>(${i.note})</i>` : ""}</td><td style="text-align:right">${eur((i.price + i.surcharge) * i.qty)}</td></tr>`)
      .join("");
    const discRows = order.discounts.map((d) => `<tr><td>${t("b.discount")} ${d.reason || ""} (${d.by})</td><td style="text-align:right">-${eur(d.amount)}</td></tr>`).join("");
    w.document.write(`<html><head><title>Conto</title></head><body style="font-family:monospace;font-size:12px">
      ${settings.logo ? `<p style="text-align:center"><img src="${settings.logo}" style="max-height:60px"/></p>` : ""}
      <h3 style="text-align:center">${settings.business_name}</h3>
      ${settings.motto && settings.motto_on_bill !== false ? `<p style="text-align:center;font-style:italic">«${settings.motto}»</p>` : ""}
      <p style="text-align:center">${settings.address}<br/>P.IVA ${settings.vat}</p>
      <p>${t("s.table")} ${order.table_numbers.join(" + ")} · ${order.covers} ${t("s.covers")} · ${order.waiter}</p>
      <table style="width:100%">${rows}
      <tr><td>${t("b.cover", { n: order.covers })}</td><td style="text-align:right">${eur(order.cover_total)}</td></tr>
      ${discRows}
      <tr><td><b>${t("b.total").toUpperCase()}</b></td><td style="text-align:right"><b>${eur(order.total)}</b></td></tr></table>
      <p style="text-align:center">${t("b.notFiscal")}</p>
      </body></html>`);
    w.document.close();
    w.print();
  };

  const closeTable = async () => {
    try {
      await p(`/orders/${order.id}/close`, { by: user.name });
      toast.success(t("b.closedOk"));
      onClose();
      onClosed?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="conto-dialog">
        <DialogHeader>
          <DialogTitle>
            {order.mode === "asporto" ? t("b.billTakeaway", { t: order.table_numbers[0] }) : t("b.billTable", { t: order.table_numbers.join(" + ") })}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-1 text-sm">
          <div className="flex justify-between text-slate-500">
            <span>{t("b.items")}</span>
            <span className="font-num">{eur(order.subtotal)}</span>
          </div>
          <div className="flex justify-between text-slate-500">
            <span>{order.mode === "asporto" ? t("b.noCover") : t("b.cover", { n: order.covers })}</span>
            <span className="font-num">{eur(order.cover_total)}</span>
          </div>
          {order.discounts.map((d) => (
            <div key={d.id} className="flex justify-between text-amber-700" data-testid={`discount-${d.id}`}>
              <span>{t("b.discount")} {d.reason && `· ${d.reason}`} ({d.by})</span>
              <span className="font-num">-{eur(d.amount)}</span>
            </div>
          ))}
          <div className="flex items-baseline justify-between pt-2">
            <span className="font-semibold">{t("b.total")}</span>
            <span className="font-num text-3xl font-bold" data-testid="conto-total">{eur(order.total)}</span>
          </div>
          <div className="flex justify-between text-slate-500">
            <span>{t("b.paid")}</span>
            <span className="font-num" data-testid="conto-paid">{eur(order.paid)}</span>
          </div>
          <div className="flex justify-between font-semibold">
            <span>{t("b.remaining")}</span>
            <span className="font-num" data-testid="conto-remaining">{eur(order.remaining)}</span>
          </div>
        </div>

        <div className="rounded-md border border-slate-200 p-3">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{t("b.discount")}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {[1, 2, 3, 5, 10].map((v) => (
              <Button key={v} size="sm" variant="outline" data-testid={`quick-discount-${v}`} onClick={() => addDiscount(v)}>
                {v} €
              </Button>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <Input className="h-9" type="number" placeholder={t("b.amount")} value={disc} onChange={(e) => setDisc(e.target.value)} data-testid="discount-amount-input" />
            <Input className="h-9" placeholder={t("b.reason")} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="discount-reason-input" />
            <Button size="sm" onClick={() => addDiscount(disc)} data-testid="apply-discount-btn">
              {t("b.apply")}
            </Button>
          </div>
        </div>

        <DividiConto order={order} pay={pay} />

        <PassaIncasso order={order} reload={reload} />

        <div className="rounded-md border border-slate-200 p-3">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{t("b.takings")}</p>
          <Input className="mt-2 h-10" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payment-amount-input" />
          <div className="mt-2 grid grid-cols-3 gap-2">
            <Button className="h-11" data-testid="pay-cash-btn" onClick={() => pay("contanti")}>
              <Banknote className="mr-1 h-4 w-4" /> {t("b.cash")}
            </Button>
            <Button className="h-11" variant="outline" data-testid="pay-card-btn" onClick={() => pay("carta")}>
              <CreditCard className="mr-1 h-4 w-4" /> {t("b.card")}
            </Button>
            <Button className="h-11" variant="outline" data-testid="pay-other-btn" onClick={() => pay("altro")}>
              <MoreHorizontal className="mr-1 h-4 w-4" /> {t("b.other")}
            </Button>
          </div>
          {order.payments.filter((x) => !x.voided).length > 0 && (
            <div className="mt-3 space-y-1 text-sm">
              {order.payments.filter((x) => !x.voided).map((x) => (
                <div key={x.id} className="flex items-center justify-between gap-2" data-testid={`payment-${x.id}`}>
                  <span className="text-slate-600">
                    {x.method} · <span className="font-num">{eur(x.amount)}</span> · {x.by}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-rose-600"
                    data-testid={`void-payment-${x.id}`}
                    onClick={async () => {
                      await p(`/orders/${order.id}/payments/${x.id}/void`);
                      toast.success(t("b.voidOk"));
                      reload();
                    }}
                  >
                    <Undo2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-md border border-slate-200 p-3">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{t("b.customer")}</p>
          {order.customer_name ? (
            <p className="mt-2 text-sm font-medium" data-testid="linked-customer">{order.customer_name}</p>
          ) : (
            <>
              <Input className="mt-2 h-9" placeholder={t("b.searchCustomer")} value={q} onChange={(e) => search(e.target.value)} data-testid="customer-search-input" />
              <div className="mt-2 flex flex-wrap gap-2">
                {found.map((c) => (
                  <Button
                    key={c.id}
                    size="sm"
                    variant="outline"
                    data-testid={`link-customer-${c.id}`}
                    onClick={async () => {
                      await p(`/orders/${order.id}/customer`, { customer_id: c.id });
                      toast.success(t("b.linked"));
                      reload();
                    }}
                  >
                    {c.name}
                  </Button>
                ))}
                {q.length > 1 && (
                  <Button
                    size="sm"
                    data-testid="create-customer-btn"
                    onClick={async () => {
                      const c = await p("/customers", { name: q });
                      await p(`/orders/${order.id}/customer`, { customer_id: c.id });
                      toast.success(t("b.created"));
                      reload();
                    }}
                  >
                    <UserPlus className="mr-1 h-3.5 w-3.5" /> {t("b.createCustomer", { q })}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>

        <div className={settings?.printing_on === false ? "grid grid-cols-1 gap-2" : "grid grid-cols-2 gap-2"}>
          {settings?.printing_on !== false && (
            <Button variant="outline" className="h-12" onClick={printBill} data-testid="print-bill-btn">
              <Printer className="mr-1.5 h-4 w-4" /> {t("b.print")}
            </Button>
          )}
          <Button className="h-12" onClick={closeTable} data-testid="close-table-btn">
            {t("b.closeTable")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
